<template>
  <b-modal
    id="add_new_status_modal"
    ref="modal"
    v-model="open"
    size="lg"
    no-close-on-backdrop
    hide-footer
    title="ADD NEW STATUS"
  >
    <b-overlay
      :show="modalLoading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <ValidationObserver
        ref="addNewStatusForm"
        v-slot="{ handleSubmit }"
        slim
      >
        <b-form
          class="my-8"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Name"
                label-for="h-name"
              >
                <b-form-input
                  id="name"
                  v-model="form.name"
                  placeholder="Enter Name"
                  type="text"
                />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Description"
                label-for="h-description"
              >
                <b-form-input
                  id="description"
                  v-model="form.description"
                  placeholder="Enter Description"
                  type="text"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Category"
                label-for="h-category"
              >
                <v-select
                  v-model="form.category"
                  :options="categoryOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Category"
                  :clearable="false"
                >
                  <template v-slot:option="option">
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Color"
                label-for="h-color"
              >
                <v-select
                  v-model="form.color"
                  :options="colorOptions"
                  :reduce="option => option.value"
                  label="name"
                  autocomplete="off"
                  placeholder="Select Color"
                  class="color-options-dropdown"
                  :clearable="false"
                >
                  <template v-slot:option="option">
                    <div
                      class="status-color-option"
                      :style="{ '--option-color': option.color }"
                    >
                      <span
                        class="color-dot"
                        :style="{ backgroundColor: option.color }"
                      />
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Before Statuses"
                label-for="h-beforeStatuses"
              >
                <v-select
                  v-model="form.before_statuses"
                  :options="statusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                  class="status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="After Statuses"
                label-for="h-afterStatuses"
              >
                <v-select
                  v-model="form.after_statuses"
                  :options="statusOptions"
                  :reduce="option => option.key"
                  label="name"
                  placeholder="Select Status"
                  multiple
                  deselect-from-dropdown
                  class="status-dropdown"
                >
                  <template
                    v-slot:option="option"
                    class="text-truncate"
                  >
                    {{ option.name }}
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group
                class="required status-form-label"
                label="Icon"
                label-for="h-icon"
              >
                <icon-picker v-model="form.icon" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row class="mt-1">
            <b-col cols="6">
              <b-form-checkbox
                v-model="form.is_default"
                :value="true"
                :unchecked-value="false"
              >
                Default Status
              </b-form-checkbox>
            </b-col>
            <b-col cols="6">
              <b-form-checkbox
                v-model="form.is_active"
                :value="true"
                :unchecked-value="false"
              >
                Active Status
              </b-form-checkbox>
            </b-col>
          </b-row>
          <!-- submit and reset -->
          <b-row>
            <b-col cols="12 my-1">
              <div class="d-flex justify-content-end">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  variant="primary"
                  class="mr-1"
                >
                  Submit
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  type="button"
                  variant="outline-secondary"
                  @click="onReset"
                >
                  Clear
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </ValidationObserver>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BButton,
  BModal,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BRow,
  BOverlay,
  BFormCheckbox,
  VBModal,
} from 'bootstrap-vue'
import * as FeatherIcons from 'vue-feather-icons'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import IconPicker from '@/components/Icons/IconPicker.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const orderRepository = RepositoryFactory.get('order')

function initialState() {
  return {
    open: false,
    modalLoading: false,
    loading: false,
    categoryOptions: [
      { name: 'First Mile', value: 'first' },
      { name: 'Middle Mile', value: 'middle' },
      { name: 'Last Mile', value: 'last' },
      { name: 'Merchant', value: 'merchant' },
    ],
    colorOptions: [
      { name: 'Primary', value: 'primary', color: '#007bff' },
      { name: 'Info', value: 'info', color: '#17a2b8' },
      { name: 'Danger', value: 'danger', color: '#dc3545' },
      { name: 'Success', value: 'success', color: '#28a745' },
      { name: 'Warning', value: 'warning', color: '#ffc107' },
      { name: 'Secondary', value: 'secondary', color: '#6c757d' },
    ],
    form: {},
    statusOptions: [],
    permissionGroupOptions: [],
  }
}

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BButton,
    BModal,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BRow,
    BOverlay,
    BFormCheckbox,
    'v-select': vSelect,
    IconPicker,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return initialState()
  },
  computed: {
    FeatherIcons: () => FeatherIcons,
  },
  methods: {
    async openModal() {
      await Object.assign(this.$data, initialState())
      this.open = true
      this.readOrderStatuses()
      this.readPermissionGroups()
    },
    async readOrderStatuses() {
      this.statusOptions = localStorage.getItem('order_statuses') ? JSON.parse(localStorage.getItem('order_statuses')) : (await orderRepository.getFullStatusList()).data.data
      if (!localStorage.getItem('order_statuses')) localStorage.setItem('order_statuses', JSON.stringify(this.statusOptions))
    },
    async readPermissionGroups() {
      this.permissionGroupOptions = (await orderRepository.getStatusPermissionGroups()).data.data
    },
    async onSubmit() {
      this.modalLoading = true
      try {
        await orderRepository.addNewStatus(this.form)
        this.showSuccessMessage('Status Added Successfully')
        this.open = false
        this.$parent.getStatusList()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.modalLoading = false
    },
    onReset() {
      // Reset the form data to its initial empty state
      this.form = initialState().form

      // Reset the vee-validate observer to clear any validation errors
      this.$nextTick(() => {
        this.$refs.addNewStatusForm.reset()
      })
    },
  },
}
</script>
<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";

.color-options-dropdown ::v-deep .vs__dropdown-option {
  padding: 0 !important;
}

.status-color-option {
  padding: 6px 12px; /* Adjust these values if needed */
  display: flex;
  align-items: center;
}

.color-options-dropdown ::v-deep .vs__dropdown-option--highlight .status-color-option {
  background-color: var(--option-color);
  color: white; /* Change text color for better contrast */
}

.color-dot {
  display: inline-block;
  width: 12px;           /* Set the size of the dot */
  height: 12px;          /* Make it a square */
  border-radius: 50%;    /* Turn the square into a circle */
  margin-right: 10px;    /* Add space between the dot and the text */
}
</style>

<style lang="scss">
@import '@core/scss/vue/required.scss';

.status-form-label label {
  font-size: 1rem !important; /* This will apply to all labels, adjust if needed */
}
</style>
